import React from 'react';
import { FormSpy, AnyObject } from 'react-final-form';
import { FormApi, FieldValidator } from 'final-form';
import clsx from 'clsx';
import { Grid, Typography, TextField, AccordionSummary, AccordionDetails } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { IAutoCompleteItem } from '../../../typings/autoComplete';
import { Address, AddressSelector, PlaceType } from '../../selectors/AddressSelector';
import LocalPostOfficeIcon from '@material-ui/icons/LocalPostOffice';
import { POBoxTypeSelector } from '../../../lookupConfiguration/selectors/POBoxTypeSelector';
import type { Enquiry, EnquiryFormClasses } from '../EnquiryFormDrawer';
import { arePropsEqualIgnoringForm } from './arePropsEqualIgnoringForm';
// DEBUG: Field / Accordion are the original components unless the render-count flag is on (see helpers/renderCountDebug)
import { Field, createRenderCountAccordion } from '../../../helpers/renderCountDebug';

const Accordion = createRenderCountAccordion('Client2PostalAddressPanel');

// Explicit request (EnquiryFormDrawer split): the second client's "Postal Address" accordion.
// Moved out of EnquiryFormDrawer.tsx with its JSX and every field's name / validate / subscription /
// handler logic unchanged. The outer component subscribes to the form (FormSpy) and passes the memoized
// body only the values it displays; event handlers read the form's values at the moment the event fires
// - exactly what the render-time `values` closure held before.
export interface Client2PostalAddressPanelProps {
    form: FormApi<Enquiry>;
    classes: EnquiryFormClasses;
    isExpanded: boolean;
    homeCountry: string;
    isSecondClientPoBoxRequired: (values: AnyObject) => boolean;
    requiredValidator: FieldValidator<any>;
    createAddressSelectionHandler: (form: FormApi<Enquiry>, values: AnyObject) => (placeType: PlaceType | null, address: Address, name: string) => void;
    createInputChangedHandler: (form: FormApi<any>, name: string | any) => (value: string) => void;
    onExpandChange: (event: React.ChangeEvent<{}>, expanded: boolean) => void;
}

interface Client2PostalAddressPanelBodyProps extends Client2PostalAddressPanelProps {
    formValues: AnyObject;
    isPoBoxRequiredForValues: boolean;
}

const Client2PostalAddressPanelBody: React.FC<Client2PostalAddressPanelBodyProps> = React.memo((bodyProps) => {
    const {
        form, classes, isExpanded, homeCountry, requiredValidator, createAddressSelectionHandler, createInputChangedHandler, onExpandChange,
        formValues,
        isPoBoxRequiredForValues,
    } = bodyProps;
    const values = formValues;

    return (
        <Accordion
            expanded={isExpanded}
            onChange={onExpandChange}
        // disabled={disabledAddress(values)}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="client2-postal-address-content"
                id="client2-postal-address-header"
                className={classes.AccordionSummary}
            >
                <Typography className={classes.heading}>
                    <LocalPostOfficeIcon className={classes.headingIcon} />
                    Postal Address
                </Typography>

                <div className={classes.infoContainer}>
                    <div className="col">
                        <div className="value">
                            {/* {getAddressLabelForPanel} */}
                            {values.secondClientPOBoxType ? `${values.secondClientPOBoxType} ` : ''}
                            {values.secondClientPOBox ? `${values.secondClientPOBox}, ` : ''}
                            {values.secondClientPostalSuburb ? `${values.secondClientPostalSuburb}, ` : ''}
                            {values.secondClientPostalState ? `${values.secondClientPostalState}, ` : ''}
                            {values.secondClientPostalPostCode ? `${values.secondClientPostalPostCode}, ` : ''}
                            {values.secondClientPostalCountry ? `${values.secondClientPostalCountry}` : ''}
                        </div>
                    </div>
                </div>

            </AccordionSummary>
            <AccordionDetails
                className={classes.expandablePanelDetails}
            >
                <Grid container={true} spacing={2}>
                    <Grid
                        item={true}
                        xs={4}
                        className={clsx(classes.customFieldGrid, classes.fieldRowGap)}
                    >
                        <Field
                            name="secondClientPOBoxType"
                            subscription={{ touched: true, error: true, value: true }}
                        // validate={required}
                        >
                            {({ input, meta }) => (
                                <POBoxTypeSelector
                                    variant="outlined"
                                    size="small"
                                    // {...input}
                                    value={input.value ? { label: input.value, value: input.value } : null}
                                    label="PO Box Type"
                                    // required={true}
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    // tslint:disable-next-line: max-line-length
                                    onSelection={(selection: IAutoCompleteItem, name: string) => {
                                        input.onChange(selection ? selection.value : null);
                                    }}
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'Field is required'
                                            : ''
                                    }
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={8} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="secondClientPOBox"
                            subscription={{ touched: true, error: true, value: true }}
                            validate={isPoBoxRequiredForValues ? requiredValidator : undefined}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label="Number"
                                    type="text"
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    required={isPoBoxRequiredForValues ? true : false}
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'Number is required'
                                            : ''
                                    }
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={4} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="secondClientPostalSuburb"
                            subscription={{ touched: true, error: true, value: true }}
                        // validate={required}
                        >
                            {({ input, meta }) => (
                                <AddressSelector
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    freeSolo={true}
                                    label="Suburb"
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    homeCountry={homeCountry}
                                    // tslint:disable-next-line: max-line-length
                                    // placeTypes={['geocode', 'establishment']} // ['geocode', 'address', 'establishment', '(regions)', '(cities)'];
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'Suburb is required'
                                            : ''
                                    }
                                    placeTypes={['(cities)']}
                                    onSelection={(placeType: PlaceType | null, address: Address, name: string) =>
                                                createAddressSelectionHandler(form, form.getState().values)(placeType, address, name)}
                                    onInputChanged={createInputChangedHandler(form, 'secondClientPostalSuburb')}
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={4} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="secondClientPostalState"
                            subscription={{ touched: true, error: true, value: true }}
                        // validate={required}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label="State"
                                    type="text"
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'State is required'
                                            : ''
                                    }
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={4} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="secondClientPostalPostCode"
                            subscription={{ touched: true, error: true, value: true }}
                        // validate={required}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label="Postal Code"
                                    type="text"
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'Postal Code is required'
                                            : ''
                                    }
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={12} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="secondClientPostalCountry"
                            subscription={{ touched: true, error: true, value: true }}
                        // validate={required}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label="Country"
                                    type="text"
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    // disabled={props.sourceSystem?.toLowerCase() === 'leap'}
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'Country is required'
                                            : ''
                                    }
                                />
                            )}
                        </Field>
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
}, arePropsEqualIgnoringForm);
Client2PostalAddressPanelBody.displayName = 'Client2PostalAddressPanelBody';

export const Client2PostalAddressPanel: React.FC<Client2PostalAddressPanelProps> = React.memo((panelProps) => (
    <FormSpy subscription={{ values: true }}>
        {({ values }) => (
            <Client2PostalAddressPanelBody
                {...panelProps}
                isPoBoxRequiredForValues={panelProps.isSecondClientPoBoxRequired(values)}
                formValues={{
                    secondClientPOBoxType: values.secondClientPOBoxType,
                    secondClientPOBox: values.secondClientPOBox,
                    secondClientPostalSuburb: values.secondClientPostalSuburb,
                    secondClientPostalState: values.secondClientPostalState,
                    secondClientPostalPostCode: values.secondClientPostalPostCode,
                    secondClientPostalCountry: values.secondClientPostalCountry,
                }}
            />
        )}
    </FormSpy>
), arePropsEqualIgnoringForm);
Client2PostalAddressPanel.displayName = 'Client2PostalAddressPanel';
